import React from 'react';
import ReactDOM from 'react-dom';
import { Info } from 'lucide-react';
import { Opt } from './mock';

export const Field: React.FC<{ label?: string; required?: boolean; full?: boolean; wide?: boolean; error?: string; hint?: string; children: React.ReactNode }> = ({
  label, required, full, wide, error, hint, children,
}) => (
  <div className={`fl-field ${full ? 'fl-field--full' : ''} ${wide ? 'fl-field--wide' : ''}`}>
    {label && (
      <label className="fl-label">
        {label}{required && <span className="fl-req">*</span>}
        {hint && (
          <span className="fl-info" tabIndex={0} title={hint} aria-label={hint}>
            <Info size={13} />
            <span className="fl-tip" role="tooltip">{hint}</span>
          </span>
        )}
      </label>
    )}
    {children}
    {error && <span className="fl-error-msg">{error}</span>}
  </div>
);

interface TextProps {
  label?: string; value?: string; onChange: (v: string) => void;
  placeholder?: string; required?: boolean; full?: boolean; wide?: boolean; error?: string; disabled?: boolean; hint?: string;
  maxLength?: number;
}

// Máscara de CNPJ: 00.000.000/0000-00 (aplica progressivamente, limita a 14 dígitos)
export const maskCnpj = (v: string): string => {
  const d = (v || '').replace(/\D/g, '').slice(0, 14);
  if (d.length <= 2) return d;
  if (d.length <= 5) return `${d.slice(0, 2)}.${d.slice(2)}`;
  if (d.length <= 8) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`;
  if (d.length <= 12) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
};

// Valida CNPJ (14 dígitos + dígito verificador) — espelha a validação do backend.
export const cnpjValido = (valor?: string): boolean => {
  const c = (valor || '').replace(/\D/g, '');
  if (c.length !== 14) return false;
  if (/^(\d)\1{13}$/.test(c)) return false; // todos os dígitos iguais
  const pesosFull = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const dv = (base: string): string => {
    const pesos = pesosFull.slice(pesosFull.length - base.length);
    const soma = base.split('').reduce((acc, d, i) => acc + Number(d) * pesos[i], 0);
    const resto = soma % 11;
    return resto < 2 ? '0' : String(11 - resto);
  };
  const d1 = dv(c.slice(0, 12));
  const d2 = dv(c.slice(0, 12) + d1);
  return c[12] === d1 && c[13] === d2;
};

export const CnpjField: React.FC<TextProps> = ({ label, value, onChange, required, full, error, hint, placeholder }) => (
  <Field label={label} required={required} full={full} error={error} hint={hint}>
    <input
      className={`fl-input ${error ? 'fl-error' : ''}`}
      inputMode="numeric" maxLength={18}
      value={value ?? ''} placeholder={placeholder ?? '00.000.000/0000-00'}
      onChange={(e) => onChange(maskCnpj(e.target.value))}
    />
  </Field>
);

// Valor em R$ — usuário digita e formata da direita p/ esquerda (centavos): "25.000,00"
export const formatMoney = (v: string): string => {
  const d = (v || '').replace(/\D/g, '');
  if (!d) return '';
  const n = parseInt(d, 10) / 100;
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export const MoneyField: React.FC<TextProps> = ({ label, value, onChange, required, full, error, hint }) => (
  <Field label={label} required={required} full={full} error={error} hint={hint}>
    <div className="fl-input-group">
      <span className="fl-addon fl-addon--left">R$</span>
      <input
        className={`fl-input ${error ? 'fl-error' : ''}`}
        inputMode="numeric" value={value ?? ''} placeholder="0,00"
        onChange={(e) => onChange(formatMoney(e.target.value))}
      />
    </div>
  </Field>
);

// Percentual — aceita dígitos e uma vírgula decimal (ex.: "0,61")
export const cleanPercent = (v: string): string => {
  let s = (v || '').replace(/[^\d.,]/g, '').replace(/\./g, ',');
  const i = s.indexOf(',');
  if (i !== -1) s = s.slice(0, i + 1) + s.slice(i + 1).replace(/,/g, '');
  return s;
};

export const PercentField: React.FC<TextProps> = ({ label, value, onChange, required, full, error, hint }) => (
  <Field label={label} required={required} full={full} error={error} hint={hint}>
    <div className="fl-input-group">
      <input
        className={`fl-input ${error ? 'fl-error' : ''}`}
        inputMode="decimal" value={value ?? ''} placeholder="0,00"
        onChange={(e) => onChange(cleanPercent(e.target.value))}
      />
      <span className="fl-addon fl-addon--right">%</span>
    </div>
  </Field>
);

export const TextField: React.FC<TextProps> = ({ label, value, onChange, placeholder, required, full, wide, error, disabled, hint, maxLength }) => (
  <Field label={label} required={required} full={full} wide={wide} error={error} hint={hint}>
    <input
      className={`fl-input ${error ? 'fl-error' : ''}`}
      value={value ?? ''} placeholder={placeholder} disabled={disabled} maxLength={maxLength}
      onChange={(e) => onChange(e.target.value)}
    />
  </Field>
);

export const NumberField: React.FC<TextProps & { step?: string }> = ({ label, value, onChange, placeholder, required, full, error, hint, step }) => (
  <Field label={label} required={required} full={full} error={error} hint={hint}>
    <input
      className={`fl-input ${error ? 'fl-error' : ''}`}
      type="number" inputMode="decimal" step={step ?? 'any'}
      value={value ?? ''} placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
    />
  </Field>
);

export const DateField: React.FC<TextProps> = ({ label, value, onChange, required, full, error, hint }) => (
  <Field label={label} required={required} full={full} error={error} hint={hint}>
    <input className={`fl-input ${error ? 'fl-error' : ''}`} type="date" value={value ?? ''} onChange={(e) => onChange(e.target.value)} />
  </Field>
);

interface SelectProps extends TextProps { options: Opt[]; }
export const SelectField: React.FC<SelectProps> = ({ label, value, onChange, options, placeholder, required, full, error, hint }) => (
  <Field label={label} required={required} full={full} error={error} hint={hint}>
    <select className={`fl-select ${error ? 'fl-error' : ''}`} value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
      <option value="">{placeholder ?? 'Selecione...'}</option>
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </Field>
);

export const TextAreaField: React.FC<TextProps> = ({ label, value, onChange, placeholder, full, error, hint }) => (
  <Field label={label} full={full} error={error} hint={hint}>
    <textarea className="fl-textarea" value={value ?? ''} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
  </Field>
);

export const CheckboxField: React.FC<{ label: string; checked: boolean; onChange: (v: boolean) => void }> = ({ label, checked, onChange }) => (
  <label className="fl-checkbox">
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
    <span>{label}</span>
  </label>
);

export const Modal: React.FC<{ title: string; onClose: () => void; onSave: () => void; saveLabel?: string; children: React.ReactNode }> = ({
  title, onClose, onSave, saveLabel = 'Salvar', children,
}) => {
  return ReactDOM.createPortal(
    <div className="fl-overlay" onMouseDown={onClose}>
      <div className="fl-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="fl-modal-header">
          <h3>{title}</h3>
          <button className="fl-icon-btn" onClick={onClose} aria-label="Fechar">✕</button>
        </div>
        <div className="fl-modal-body">{children}</div>
        <div className="fl-modal-footer">
          <button className="fl-btn fl-btn--ghost" onClick={onClose}>Cancelar</button>
          <button className="fl-btn fl-btn--primary" onClick={onSave}>{saveLabel}</button>
        </div>
      </div>
    </div>,
    document.body,
  );
};
